import React, { useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
	Grid,
	Typography,
	Switch,
	List,
	ListItem,
	ListItemText,
	ListItemSecondaryAction,
} from '@mui/material';
import { Theme } from '@mui/material/styles';

import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';

import useDebouncedForm from 'common/hooks/useDebouncedForm';
import { UserDetailName } from 'common/types';
import { getAllUserDetailNames } from 'common/utils/userDetails';

import ContainerBox from 'components/ContainerBox';
import * as ActiveProductActions from 'actions/ActiveProductActions';
import * as ActiveProductSelectors from 'selectors/ActiveProductSelectors';
import { useTranslation } from 'services/localization/useTranslation';
import { getUserDetailTranslation } from 'common/utils/translations';

const UserDetailsBlock = () => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const { t } = useTranslation();

	const product = useSelector(ActiveProductSelectors.productDataWithChanges);
	const isEditingDisabled = useSelector(ActiveProductSelectors.isEditingDisabled);

	const userDetailNames = getAllUserDetailNames();

	const { state, editState } = useDebouncedForm({
		memoizedInitialState: useMemo(
			() => ({
				userDetails: product?.userDetails || [],
			}),
			[product],
		),
		memoizedOnDebounce: useCallback(
			(changes) => {
				dispatch(ActiveProductActions.editLocalProduct(changes));
			},
			[dispatch],
		),
	});

	const renderSwitch = (name: UserDetailName) => {
		const current = state.userDetails;
		const isChecked = current.some((detail) => detail.name === name);

		return (
			<Switch
				checked={isChecked}
				onChange={(e) => {
					const { checked } = e.target;
					editState({
						userDetails: checked
							? current.concat({
									name,
									required: true,
							  })
							: current.filter((detail) => detail.name !== name),
					});
				}}
				name="checkedB"
				color="primary"
				disabled={isEditingDisabled}
			/>
		);
	};

	return (
		<ContainerBox>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Typography variant="h6">
						{t('product.informationAskedFromCustomer', 'Customer information')}
					</Typography>
					<Typography variant="body2" color="secondary" sx={{ mb: 2 }}>
						{t(
							'product.informationAskedFromCustomerHelper',
							'Information asked from customers before starting the rental',
						)}
					</Typography>
				</Grid>
				<Grid item xs={12}>
					<List>
						{userDetailNames.map((detailName) => (
							<ListItem key={detailName} className={classes.listItem} divider>
								<ListItemText primary={getUserDetailTranslation(detailName, t)} />
								<ListItemSecondaryAction>{renderSwitch(detailName)}</ListItemSecondaryAction>
							</ListItem>
						))}
					</List>
				</Grid>
			</Grid>
		</ContainerBox>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		listItem: {
			paddingTop: theme.spacing(2),
			paddingBottom: theme.spacing(2),
			'&:hover': {
				background: 'rgba(0,0,0,0.03)',
			},
		},
	}),
);

export default UserDetailsBlock;
